import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { IMG, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/content";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { FadeUp, FINE_POINTER } from "@/components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

const HERO_SLIDES = [
  {
    id: "drop-001",
    eyebrow: "DROP 001 — THE JOJOS SET",
    title: ["JOJOS SET", "DROP 001."],
    sub: "Luxury-quality anime streetwear, sourced and inspected by hand. A different kind of uniform.",
    cta: "SHOP THE DROP",
    ctaTo: "/shop",
    ctaId: "hero-cta-shop",
    shot: "SHOT 01 — JOJOS X MEDM SET PIECE JEANS · LIGHT-WASH BLUE · CAPSULE CAMPAIGN",
    img: IMG.hero,
    alt: "MONOLITH WIDE JEAN — editorial campaign photograph",
    imgClass: "object-top",
  },
  {
    id: "drop-002",
    eyebrow: "DROP 002 — OFFICIAL EVANGELION MERCH",
    title: ["EVANGELION", "DROP 002."],
    sub: "Industrial monochrome. Manga-panel prints. The darker, futuristic edit.",
    cta: "SHOP DROP 002",
    ctaTo: "/collections/evangelion-drop",
    ctaId: "hero-cta-drop-002",
    shot: "SHOT 02 — OFFICIAL EVANGELION MERCH SET PIECE HOODIE · LIGHT GREY · EVANGELION DROP 002",
    img: "/products/v3/hoodie-model-back.webp",
    alt: "OFFICIAL EVANGELION MERCH SET PIECE HOODIE — campaign photograph",
    imgClass: "object-[center_70%]",
  },
];

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 120]);
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    const t = setInterval(() => {
      if (!paused.current) setIdx((i) => (i + 1) % HERO_SLIDES.length);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const line = (text, i) => (
    <span key={text} className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay: 0.2 + i * 0.14, ease: EASE }}
      >
        {text}
      </motion.span>
    </span>
  );
  const slide = HERO_SLIDES[idx];

  return (
    <section
      className="grid min-h-[100svh] bg-bone lg:grid-cols-12"
      data-testid="hero-section"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
    >
      <div className="relative flex items-center px-5 py-20 sm:px-10 lg:col-span-5 lg:py-0 lg:pl-14 xl:pl-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="relative z-10"
          >
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-mono text-[10px] uppercase tracking-[0.35em] text-plum sm:text-[11px]"
            >
              {slide.eyebrow}
            </motion.p>
            <h1 className="mt-6 font-display text-7xl leading-[0.9] tracking-[0.01em] text-ink sm:text-8xl lg:text-[5.4rem] xl:text-8xl">
              {slide.title.map((t, i) => line(t, i))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-8 max-w-sm text-sm leading-relaxed text-ink/65 sm:text-base"
            >
              {slide.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-10 flex flex-col items-start gap-7 sm:flex-row sm:items-center sm:gap-9"
            >
              <Link
                to={slide.ctaTo}
                data-testid={slide.ctaId}
                className="bg-ink px-10 py-4 font-display text-xl tracking-[0.15em] text-bone transition-colors duration-300 hover:bg-plum"
              >
                {slide.cta}
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-discover"
                className="group inline-flex items-center gap-2 border-b border-ink/25 pb-1 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/80 transition-colors hover:text-plum"
              >
                DISCOVER REQUIEM FEVER
                <span className="h-px w-5 bg-plum transition-all duration-300 group-hover:w-8" aria-hidden="true" />
              </Link>
            </motion.div>
          </motion.div>
        </AnimatePresence>
        <div className="absolute bottom-10 left-5 z-10 flex items-center gap-5 sm:left-10 lg:left-14 xl:left-20">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.id}
              data-testid={`hero-slide-indicator-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Show ${s.title.join(" ")}`}
              className={`border-b pb-1 font-mono text-[10px] tracking-[0.3em] transition-colors duration-300 ${
                i === idx ? "border-plum text-plum" : "border-ink/20 text-ink/40 hover:text-ink"
              }`}
            >
              00{i + 1}
            </button>
          ))}
        </div>
      </div>
      <div className="relative overflow-hidden bg-plum lg:col-span-7" data-testid="hero-photo">
        <motion.div
          initial={{ opacity: FINE_POINTER ? 0 : 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: FINE_POINTER ? 1.4 : 0, ease: EASE }}
          className="h-[72svh] w-full lg:h-full lg:min-h-[100svh]"
        >
          <motion.div
            className="flex h-full w-full"
            animate={{ x: `-${idx * 100}%` }}
            transition={{ duration: 1.1, ease: EASE }}
          >
            {HERO_SLIDES.map((s) => (
              <div key={s.id} className="relative h-[72svh] w-full shrink-0 overflow-hidden lg:h-full lg:min-h-[100svh]">
                <motion.img
                  src={s.img}
                  alt={s.alt}
                  loading={s.id === "drop-001" ? "eager" : "lazy"}
                  fetchPriority={s.id === "drop-001" ? "high" : "low"}
                  style={{ y }}
                  className={`h-full w-full object-cover ${s.imgClass}`}
                />
                <p className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/85">
                  {s.shot}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function FeaturedDrop({ products }) {
  const [jeans, teeChar, sweatshirt] = products;
  const loaded = products.length >= 3;

  return (
    <section className="bg-bone py-20 sm:py-28" data-testid="featured-drop">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <FadeUp>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-plum">DROP 001</p>
            </FadeUp>
            <FadeUp delay={0.08}>
              <h2 className="mt-3 font-display text-5xl leading-[0.9] tracking-[0.01em] text-ink sm:text-7xl">
                THE JOJOS
                <br />
                SET
              </h2>
            </FadeUp>
            <FadeUp delay={0.14}>
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/55">
                {jeans && jeans.price != null
                  ? `₹${jeans.price.toLocaleString("en-IN")} · FREE SHIPPING ACROSS INDIA · SIZES S — 3XL`
                  : "FREE SHIPPING ACROSS INDIA · SIZES S — 3XL"}
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <Link
              to="/shop"
              data-testid="featured-view-all"
              className="group inline-flex items-center gap-2 border-b border-ink/30 pb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:border-magenta hover:text-plum"
            >
              VIEW ALL
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>

        {loaded && (
          <div className="mt-12 grid gap-x-5 gap-y-14 sm:mt-16 lg:grid-cols-12">
            {jeans && (
              <ProductCard
                product={jeans}
                index={0}
                className="lg:col-span-7 lg:row-span-2"
                aspect="aspect-[3/4] lg:aspect-auto lg:h-full"
                fill
              />
            )}
            {sweatshirt && <ProductCard product={sweatshirt} index={2} className="lg:col-span-5" aspect="aspect-square" />}
            {teeChar && <ProductCard product={teeChar} index={1} className="lg:col-span-5" aspect="aspect-square" />}
          </div>
        )}
      </div>
    </section>
  );
}

function CollectionsSection() {
  const cards = [
    {
      to: "/collections/jojo-drop-001",
      no: "COLLECTION 01",
      title: "JOJO DROP 001",
      line: "TWO DROPS. ZERO FILLER.",
      img: IMG.sbrDetail,
      testid: "home-collection-card-jojo-drop-001",
    },
    {
      to: "/collections/evangelion-drop",
      no: "COLLECTION 02",
      title: "EVANGELION DROP 002",
      line: "INDUSTRIAL MONOCHROME EDIT",
      img: "/products/v3/hoodie-model-back.webp",
      testid: "home-collection-card-evangelion-drop",
    },
  ];
  return (
    <section className="bg-ink py-20 sm:py-28" data-testid="collections-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <FadeUp>
            <h2 className="font-display text-4xl leading-none tracking-[0.02em] text-bone sm:text-6xl">
              TWO WORLDS.
              <br />
              ONE LABEL.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link to="/shop" data-testid="collections-all-link" className="group inline-flex items-center gap-2 border-b border-gold/50 pb-1 font-mono text-[10px] uppercase tracking-[0.3em] text-gold transition-colors hover:border-bone hover:text-bone">
              ALL COLLECTIONS
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {cards.map((c, i) => (
            <FadeUp key={c.to} delay={i * 0.1}>
              <Link to={c.to} data-testid={c.testid} className="group relative block overflow-hidden border border-line bg-surface">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={c.img} alt={c.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[center_60%] transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">{c.no}</p>
                    <h3 className="mt-2 font-display text-3xl leading-none tracking-[0.02em] text-bone sm:text-4xl">{c.title}</h3>
                    <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-bone/60">{c.line}</p>
                  </div>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandStatement() {
  return (
    <section className="relative overflow-hidden bg-plum py-20 sm:py-28" data-testid="brand-statement">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">THE LABEL</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="mt-4 font-display text-5xl leading-[0.9] tracking-[0.01em] text-bone sm:text-7xl">
              SOURCED FOR THE
              <br />
              UNCONVENTIONAL.
            </h2>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-bone/75 sm:text-base">
              We don't make clothes — we source the best anime streetwear on the market and reject
              the rest. Heavyweight fabrics, prints that last, pieces with real presence: no
              mass-printed filler, ever.
            </p>
          </FadeUp>
          <FadeUp delay={0.22}>
            <Link
              to="/about"
              data-testid="brand-statement-about-link"
              className="mt-8 inline-flex items-center gap-2 border border-gold/60 px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.3em] text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              READ OUR STORY
            </Link>
          </FadeUp>
        </div>
        <div className="relative lg:col-span-5">
          <FadeUp delay={0.1} className="relative ml-auto w-4/5">
            <div className="overflow-hidden border border-gold/25">
              <img src={IMG.jeansSide} alt="JOJOS set piece detail" loading="lazy" className="aspect-[3/4] w-full object-cover" />
            </div>
            <span className="absolute -right-2 -top-2 h-full w-full border border-gold/40" aria-hidden="true" />
          </FadeUp>
          <FadeUp delay={0.25} className="absolute -bottom-10 left-0 w-1/2">
            <div className="overflow-hidden border border-white/15">
              <img src={IMG.jeansZipper} alt="JOJOS set piece zipper detail" loading="lazy" className="aspect-square w-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function InstagramSection() {
  const tiles = [IMG.wonderComposed, IMG.sweatComposed, IMG.jeansSide, IMG.sbrComposed, IMG.beltStarComposed, IMG.skullComposed];
  return (
    <section className="bg-ink py-20 sm:py-28" data-testid="instagram-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <FadeUp>
            <h2 className="font-display text-4xl leading-none tracking-[0.02em] text-bone sm:text-6xl">
              ON THE FEED
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">{INSTAGRAM_HANDLE}</p>
          </FadeUp>
        </div>
        <FadeUp delay={0.15}>
          <div className="mt-10 grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-6">
            {tiles.map((src, i) => (
              <a
                key={`${src}-${i}`}
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`instagram-tile-${i}`}
                className="group relative block aspect-square overflow-hidden bg-surface"
              >
                <img
                  src={src}
                  alt={`Requiem Fever Instagram post ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-ink/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <Instagram className="h-6 w-6 text-gold" strokeWidth={1.25} />
                </span>
              </a>
            ))}
          </div>
        </FadeUp>
        <FadeUp delay={0.2}>
          <div className="mt-10 text-center">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-follow-button"
              className="inline-flex items-center gap-3 border border-line px-9 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-bone transition-colors hover:border-violet hover:text-gold"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} />
              FOLLOW THE FEVER
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, []);

  return (
    <>
      <Hero />
      <Marquee items={["NOT MADE TO BLEND IN", "REQUIEM FEVER", "JOJOS SET DROP 001", "EVANGELION DROP 002"]} />
      <FeaturedDrop products={products} />
      <CollectionsSection />
      <BrandStatement />
      <InstagramSection />
    </>
  );
}
